import React, { useState, useRef, useEffect } from 'react';
import {
  LayoutDashboard,
  Users,
  FileText,
  Calendar,
  UserCheck,
  Anchor,
  MoreHorizontal,
  Wrench,
  X,
  Plus
} from 'lucide-react';
import { playPulseSeal } from '../utils/pulseAudio';
import { UserSession, UserRole } from '../types';
import { MobileAccountRoleSection } from './MobileAccountRoleSection';

interface MobileReferralCoordinatorBottomNavProps {
  currentTab: string;
  onSelectTab: (tab: string) => void;
  onOpenUniversalTools?: () => void;
  onOpenIntakePacket?: () => void;
  onOpenNewClient?: () => void;
  user?: UserSession;
  onSwitchRole?: (role: UserRole) => void;
  onLogout?: () => void;
}

interface NavDestination {
  id: string;
  label: string;
  tab: string;
  icon: React.ComponentType<{ className?: string; strokeWidth?: number; size?: number }>;
  isActive: (current: string) => boolean;
}

export const MobileReferralCoordinatorBottomNav: React.FC<MobileReferralCoordinatorBottomNavProps> = ({
  currentTab,
  onSelectTab,
  onOpenUniversalTools,
  onOpenIntakePacket,
  onOpenNewClient,
  user,
  onSwitchRole,
  onLogout
}) => {
  const [isMoreOpen, setIsMoreOpen] = useState(false);
  const moreSheetRef = useRef<HTMLDivElement>(null);

  // Close more menu when clicking outside
  useEffect(() => {
    const handleTouchOutside = (e: MouseEvent | TouchEvent) => {
      if (moreSheetRef.current && !moreSheetRef.current.contains(e.target as Node)) {
        setIsMoreOpen(false);
      }
    };
    if (isMoreOpen) {
      document.addEventListener('mousedown', handleTouchOutside);
      document.addEventListener('touchstart', handleTouchOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleTouchOutside);
      document.removeEventListener('touchstart', handleTouchOutside);
    };
  }, [isMoreOpen]);

  const destinations: NavDestination[] = [
    {
      id: 'rc-nav-dashboard',
      label: 'Dashboard',
      tab: 'dashboard',
      icon: LayoutDashboard,
      isActive: (cur) => cur === 'dashboard'
    },
    {
      id: 'rc-nav-clients',
      label: 'Clients',
      tab: 'clients',
      icon: Users,
      isActive: (cur) => cur === 'clients'
    },
    {
      id: 'rc-nav-documents',
      label: 'Documents',
      tab: 'clinical',
      icon: FileText,
      isActive: (cur) => cur === 'clinical'
    },
    {
      id: 'rc-nav-providers',
      label: 'Providers',
      tab: 'supervision',
      icon: UserCheck,
      isActive: (cur) => cur === 'supervision'
    },
    {
      id: 'rc-nav-more',
      label: 'More',
      tab: 'more',
      icon: MoreHorizontal,
      isActive: (_cur) => isMoreOpen
    }
  ];

  const handleTap = (tab: string) => {
    playPulseSeal({ cooldownMs: 120, volume: 0.35 });
    if (tab === 'more') {
      setIsMoreOpen(!isMoreOpen);
    } else {
      setIsMoreOpen(false);
      onSelectTab(tab);
    }
  };

  return (
    <>
      {/* More Action Sheet Modal (Tactile Popover above Bottom Nav) */}
      {isMoreOpen && (
        <div className="fixed inset-0 z-40 bg-slate-900/40 backdrop-blur-xs md:hidden animate-in fade-in duration-150">
          <div
            ref={moreSheetRef}
            className="absolute bottom-[calc(52px+env(safe-area-inset-bottom,0px))] inset-x-3 max-h-[calc(100dvh-64px)] overflow-y-auto bg-[#f8fafc] rounded-2xl border border-slate-200/90 shadow-[0_-8px_30px_rgba(15,23,42,0.12),0_4px_16px_rgba(15,23,42,0.08)] p-3.5 space-y-2.5 animate-in slide-in-from-bottom-3 duration-200"
          >
            <div className="flex items-center justify-between pb-2.5 border-b border-slate-200/90 px-1 min-h-[36px]">
              <div className="flex items-center gap-2 min-w-0">
                <span className="w-1.5 h-4.5 rounded-full bg-slate-700 shrink-0" aria-hidden="true" />
                <div className="flex items-baseline gap-2 truncate">
                  <h2 className="text-sm font-bold tracking-tight text-slate-900 leading-none">
                    Referral Coordinator Hub
                  </h2>
                  <span className="text-[10px] font-mono font-semibold uppercase tracking-wider text-slate-700 bg-slate-100 px-1.5 py-0.5 rounded border border-slate-200 leading-none shrink-0">
                    More
                  </span>
                </div>
              </div>
              <button
                type="button"
                onClick={() => setIsMoreOpen(false)}
                className="p-1.5 rounded-lg text-slate-500 hover:text-slate-800 bg-slate-100 hover:bg-slate-200 border border-slate-200/90 transition-colors cursor-pointer shrink-0"
                aria-label="Close More Menu"
              >
                <X className="w-3.5 h-3.5" />
              </button>
            </div>

            {/* Quick Actions (Intake & Client) */}
            {(onOpenIntakePacket || onOpenNewClient) && (
              <div className="grid grid-cols-2 gap-2 pt-1 pb-1 border-b border-slate-200/90">
                {onOpenIntakePacket && (
                  <button
                    type="button"
                    onClick={() => {
                      playPulseSeal({ cooldownMs: 120, volume: 0.35 });
                      setIsMoreOpen(false);
                      onOpenIntakePacket();
                    }}
                    className="flex items-center justify-center space-x-1.5 py-2 px-3 rounded-xl bg-emerald-600 hover:bg-emerald-500 text-white text-xs font-semibold cursor-pointer active:scale-95 transition-all shadow-xs"
                  >
                    <Plus className="w-3.5 h-3.5 text-emerald-100" />
                    <span>New Intake</span>
                  </button>
                )}
                {onOpenNewClient && (
                  <button
                    type="button"
                    onClick={() => {
                      playPulseSeal({ cooldownMs: 120, volume: 0.35 });
                      setIsMoreOpen(false);
                      onOpenNewClient();
                    }}
                    className="flex items-center justify-center space-x-1.5 py-2 px-3 rounded-xl bg-white hover:bg-slate-50 border border-slate-300 text-slate-800 text-xs font-semibold cursor-pointer active:scale-95 transition-all shadow-xs"
                  >
                    <Plus className="w-3.5 h-3.5 text-slate-600" />
                    <span>New Client</span>
                  </button>
                )}
              </div>
            )}

            <div className="grid grid-cols-1 gap-2 pt-1">
              {/* Planner Option */}
              <button
                type="button"
                id="rc-more-planner"
                onClick={() => {
                  playPulseSeal({ cooldownMs: 120, volume: 0.35 });
                  setIsMoreOpen(false);
                  onSelectTab('assignments');
                }}
                className={`w-full flex items-center justify-between p-3 rounded-xl border transition-all text-left cursor-pointer active:scale-[0.99] ${
                  currentTab === 'assignments' || currentTab === 'scheduling' || currentTab === 'visit-planner'
                    ? 'bg-slate-100 border-slate-300 text-slate-950 font-semibold shadow-xs'
                    : 'bg-white hover:bg-slate-50 border-slate-200 text-slate-800 shadow-xs'
                }`}
              >
                <div className="flex items-center space-x-3">
                  <div className="w-9 h-9 rounded-lg bg-slate-100 border border-slate-200 flex items-center justify-center text-slate-700">
                    <Calendar className="w-4 h-4" />
                  </div>
                  <div>
                    <div className="text-xs font-bold text-slate-900 flex items-center gap-1.5">
                      <span>Planner</span>
                      <span className="text-[9px] font-mono px-1.5 py-0.2 rounded bg-slate-100 text-slate-700 border border-slate-200 font-semibold">
                        Caseload
                      </span>
                    </div>
                    <div className="text-[10.5px] text-slate-500">
                      Assignments, scheduling & caseload planner
                    </div>
                  </div>
                </div>
              </button>

              <button
                type="button"
                id="rc-more-wellsky-import"
                onClick={() => {
                  playPulseSeal({ cooldownMs: 120, volume: 0.35 });
                  setIsMoreOpen(false);
                  onSelectTab('authorizations');
                }}
                className={`w-full flex items-center justify-between p-3 rounded-xl border transition-all text-left cursor-pointer active:scale-[0.99] ${
                  currentTab === 'authorizations'
                    ? 'bg-slate-100 border-slate-300 text-slate-950 font-semibold shadow-xs'
                    : 'bg-white hover:bg-slate-50 border-slate-200 text-slate-800 shadow-xs'
                }`}
              >
                <div className="flex items-center space-x-3">
                  <div className="w-9 h-9 rounded-lg bg-slate-100 border border-slate-200 flex items-center justify-center text-slate-700">
                    <Anchor className="w-4 h-4" />
                  </div>
                  <div>
                    <div className="text-xs font-bold text-slate-900">WellSky Import</div>
                    <div className="text-[10.5px] text-slate-500">
                      Report drop, preview, exceptions, and import history
                    </div>
                  </div>
                </div>
              </button>

              {/* Universal Tools Option */}
              <button
                type="button"
                id="rc-more-universal-tools"
                onClick={() => {
                  playPulseSeal({ cooldownMs: 120, volume: 0.35 });
                  setIsMoreOpen(false);
                  if (onOpenUniversalTools) {
                    onOpenUniversalTools();
                  } else {
                    window.dispatchEvent(new CustomEvent('pulse_open_mobile_universal_tools'));
                  }
                }}
                className="w-full flex items-center justify-between p-3 rounded-xl border border-slate-200 hover:border-slate-300 bg-white hover:bg-slate-50 text-slate-800 transition-all text-left cursor-pointer active:scale-[0.99] shadow-xs"
              >
                <div className="flex items-center space-x-3">
                  <div className="w-9 h-9 rounded-lg bg-slate-100 border border-slate-200 flex items-center justify-center text-slate-700">
                    <Wrench className="w-4 h-4" />
                  </div>
                  <div>
                    <div className="text-xs font-bold text-slate-900 flex items-center gap-1.5">
                      <span>Universal Tools</span>
                      <span className="text-[9px] font-mono px-1.5 py-0.2 rounded bg-slate-100 text-slate-700 border border-slate-200 font-semibold">
                        Suite
                      </span>
                    </div>
                    <div className="text-[10.5px] text-slate-500">
                      Call, Fax, Email / Draft, Add Note, Clinical Doc, Sound
                    </div>
                  </div>
                </div>
              </button>
            </div>

            {/* Account & Role Section (Includes Role Switcher & Sign Out) */}
            {user && (
              <MobileAccountRoleSection
                user={user}
                onSwitchRole={onSwitchRole}
                onLogout={onLogout}
                onAfterAction={() => setIsMoreOpen(false)}
              />
            )}
          </div>
        </div>
      )}

      <nav
        id="pulse-mobile-rc-bottom-nav"
        aria-label="Referral Coordinator Mobile Navigation"
        className="fixed bottom-0 left-0 right-0 z-40 md:hidden bg-white/95 border-t border-slate-200/90 shadow-[0_-2px_10px_rgba(15,23,42,0.06)] select-none backdrop-blur-md"
        style={{ paddingBottom: 'env(safe-area-inset-bottom, 0px)' }}
      >
        {/* 5 Destination Grid */}
        <div className="grid grid-cols-5 h-[52px] items-stretch">
          {destinations.map((item) => {
            const active = item.isActive(currentTab);
            const Icon = item.icon;

            return (
              <button
                key={item.id}
                id={item.id}
                type="button"
                onClick={() => handleTap(item.tab)}
                className="relative flex flex-col items-center justify-center h-full py-1 px-1 transition-all duration-150 active:scale-90 group focus:outline-none cursor-pointer"
                aria-current={active ? 'page' : undefined}
              >
                {/* Active top edge indicator bar */}
                {active && (
                  <div
                    className="absolute top-0 h-[2.5px] w-8 rounded-full bg-slate-900 transition-all duration-200"
                  />
                )}

                {/* Icon */}
                <div className="relative flex items-center justify-center shrink-0">
                  <Icon
                    className={`w-5 h-5 transition-all duration-150 ${
                      active
                        ? 'text-slate-900 scale-105'
                        : 'text-slate-500 group-hover:text-slate-700'
                    }`}
                    strokeWidth={active ? 2.3 : 1.9}
                  />
                </div>

                {/* Destination Label - Single line guaranteed */}
                <span
                  className={`text-[10px] tracking-tight leading-none mt-1 whitespace-nowrap transition-colors select-none ${
                    active
                      ? 'font-bold text-slate-900'
                      : 'font-medium text-slate-500 group-hover:text-slate-700'
                  }`}
                >
                  {item.label}
                </span>
              </button>
            );
          })}
        </div>
      </nav>
    </>
  );
};
